import React, { useEffect, useRef, useState } from "react";
import { Page, Card, Layout } from "@fdoh/health-ui-react";
import { useMsal } from "@azure/msal-react";
import { toast } from "react-toastify";
import useClient from "../../hooks/useInspectionClient";
import { UserSecurityInput } from "../../components/DBProtectedRoute.types";
import { SignUpPage } from "./SignUp";
import type { SignUpInput } from "./SignUp.types";
import type { RegistryProfileApiResponse } from "./SignUp.types";
import { mapProfileResponseToSignUpInput, getSignUpInitialDataFromProfile } from "./SignUp.types";

/**
 * Profile page: allows an authenticated user to view and edit their PPI registration
 * (same data as sign-up). Loads profile by current user email; if none found, prefills from UserSecurity table.
 * Uses the inspection client (InspectionJwtClient), which adds the API key to all requests via ApiEndpoint.
 */
export const ProfilePage = () => {
    const { accounts } = useMsal();
    const userEmail = accounts[0]?.username ?? "";
    const client = useClient();
    const clientRef = useRef(client);
    clientRef.current = client;
    const [initialData, setInitialData] = useState<SignUpInput | undefined>(undefined);
    const [prefillData, setPrefillData] = useState<SignUpInput | undefined>(undefined);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    const fetchUserSecurityPrefill = async (email: string): Promise<SignUpInput | undefined> => {
        try {
            const userResponse = await clientRef.current.api(`Authentication/GetUserSecurityByEmail/${encodeURIComponent(email)}`).get();
            const userData = userResponse?.data as UserSecurityInput | undefined;
            if (userData?.email) {
                return getSignUpInitialDataFromProfile(userData);
            }
        } catch {
            // UserSecurity lookup failed; caller will use email-only prefill.
        }
        return undefined;
    };

    useEffect(() => {
        if (!userEmail) {
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            const emailUsed = userEmail;
            try {
                const response = await clientRef.current.api(`RegistryProfile/GetByEmail/${encodeURIComponent(userEmail)}`).get();
                if (cancelled) return;
                const data = response.data as RegistryProfileApiResponse;
                if (data?.email) {
                    setInitialData(mapProfileResponseToSignUpInput(data));
                    setPrefillData(undefined);
                    setNotFound(false);
                } else {
                    const userSecurityPrefill = await fetchUserSecurityPrefill(emailUsed);
                    if (!cancelled) {
                        setPrefillData(userSecurityPrefill ?? getSignUpInitialDataFromProfile({ email: emailUsed }));
                        setNotFound(true);
                    }
                }
            } catch (err: unknown) {
                if (cancelled) return;
                const status = (err as Error & { status?: number }).status;
                const is404 = status === 404;
                if (is404) {
                    const userSecurityPrefill = await fetchUserSecurityPrefill(emailUsed);
                    if (!cancelled) {
                        setPrefillData(userSecurityPrefill ?? getSignUpInitialDataFromProfile({ email: emailUsed }));
                        setNotFound(true);
                    }
                } else {
                    toast.error("Failed to load your registration. Please try again.");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [userEmail]);

    const handleProfileUpdate = async (_data: SignUpInput, formData: FormData) => {
        try {
            const response = await client.api("RegistryProfile/Update").put(formData);
            if (response?.data != null) {
                toast.success("Your registration has been updated successfully.");
            }
        } catch {
            toast.error("Failed to update your registration. Please try again.");
        }
    };

    if (loading) {
        return (
            <Page.Container>
                <Page.Body>
                    <Layout.Container>
                        <div className="row justify-content-center">
                            <div className="col col-md-6 text-center py-5">
                                <p className="text-muted" role="status" aria-live="polite">Loading your registration…</p>
                            </div>
                        </div>
                    </Layout.Container>
                </Page.Body>
            </Page.Container>
        );
    }

    if (!userEmail) {
        return (
            <Page.Container>
                <Page.Body>
                    <Layout.Container>
                        <div className="row justify-content-center">
                            <div className="col col-md-6">
                                <Card.Container>
                                    <Card.Body>
                                        <p className="text-muted">You must be signed in to edit your registration.</p>
                                    </Card.Body>
                                </Card.Container>
                            </div>
                        </div>
                    </Layout.Container>
                </Page.Body>
            </Page.Container>
        );
    }

    if (notFound) {
        const dataToPrefill = prefillData ?? getSignUpInitialDataFromProfile({ email: userEmail });
        // Key so form remounts when prefillData arrives (so defaultValues/reset get full user details)
        const prefillKey = prefillData ? "prefilled" : "email-only";
        return (
            <SignUpPage
                key={`signup-not-found-${prefillKey}`}
                mode="signup"
                initialData={dataToPrefill}
                signupPrefilledNote="No registration was found for your account. Your email and name have been prefilled below; complete the rest of the form to submit your registration."
            />
        );
    }

    return (
        <SignUpPage
            mode="profile"
            initialData={initialData}
            onSubmitOverride={handleProfileUpdate}
        />
    );
};

export default ProfilePage;
